import * as Sentry from '@sentry/vue'
import router from '@/router'

import apis from '@/services'
import OrganisationManagementService from '@/services/OrganisationManagementService'
import { getPayloadFromJwt, getTokenFromLocalStorage, isTokenValid, setStudioTokenInServices } from '@/utils/tokenUtils'
import { AuthStateValidationError, GetOrganisationListApiError, UserNotFoundError } from './exceptions'
import queryClient from '@/plugins/queryClient.js'
import { fetchOrganisationAssignments, selectedAccommodationId, setSelectedAccommodationId } from '@/layouts/queries'
import appAnalytics from '@/utils/tracking'

async function restoreStudioSession({ state, commit, dispatch }, payload) {
  commit('SET_LOADING')

  try {
    const hasJLoToken = await dispatch('ensureJLoTokenAvailable')
    if (!hasJLoToken) {
      if (payload?.to && payload.to.path !== '/') {
        dispatch('logout', payload.to.fullPath ?? payload.to.path)
      } else {
        dispatch('logout')
      }
      return
    }
    if (state.organisations.length === 0) {
      await dispatch('getOrganisationsList')
      commit('SET_IS_ONBOARDING_USER_DETAILS', false)
      dispatch('selectInitialSelectedOrganisation')
    }

    if (!state.selectedOrganisation?.id && state.organisations.length > 0) {
      dispatch('selectInitialSelectedOrganisation')
    }

    if (!state.selectedOrganisation?.id) {
      commit('SET_IS_ONBOARDING_ASSIGNMENTS', true)
      commit('SET_SUCCESS')
      if (!payload?.to && router.currentRoute.value.name !== 'onboarding') await router.replace({ name: 'onboarding' })
      return
    }

    await dispatch('restoreStudioToken')
    if (!state.studioToken) throw new Error('studioToken missing in state')
    const orgAssignments = await dispatch('getAssignmentsList')
    if (!Array.isArray(orgAssignments) || orgAssignments.length === 0) {
      commit('SET_IS_ONBOARDING_ASSIGNMENTS', true)
      setSelectedAccommodationId(null)
      commit('SET_SUCCESS')
      if (!payload?.to && router.currentRoute.value.name !== 'onboarding') await router.replace({ name: 'onboarding' })
      else return
    } else {
      commit('SET_IS_ONBOARDING_ASSIGNMENTS', false)
    }
    if (!selectedAccommodationId.value) {
      await dispatch('selectInitialSelectedAccommodationId')
    }
    if (!payload?.to && router.currentRoute.value.name === 'onboarding') await router.replace('/')
  } catch (error) {
    if (error instanceof GetOrganisationListApiError) {
      dispatch('logout')
      return
    }
    if (error instanceof UserNotFoundError) {
      commit('SET_IS_ONBOARDING_USER_DETAILS', true)
      commit('SET_SUCCESS')
      if (!payload?.to && router.currentRoute.value.name === 'onboarding') await router.replace({ name: 'onboarding' })
      else return
    } else {
      commit('SET_ERROR')
      Sentry.withScope(scope => {
        scope.setTag('feature', 'restoreStudioSession')
        Sentry.captureException(error)
      })
      return
    }
  }

  commit('SET_SUCCESS')
}

async function restoreJLoToken({ commit }) {
  const tokenFromLocalStorage = getTokenFromLocalStorage('jlo_token')

  if (isTokenValid(tokenFromLocalStorage)) {
    commit('SET_JLO_TOKEN', { token: tokenFromLocalStorage })
  }
}

async function refreshJLoToken({ state, commit }) {
  const refreshToken = state.JLoToken?.refresh_token ?? getTokenFromLocalStorage('jlo_token')?.refresh_token

  if (!refreshToken) {
    return false
  }

  try {
    const newToken = await apis.oauth2.requestTokenRefresh(refreshToken)
    commit('SET_JLO_TOKEN', { token: newToken })
    return true
  } catch {
    return false
  }
}

async function ensureValidJLoToken({ state, dispatch }) {
  if (isTokenValid(state.JLoToken)) {
    return true
  }

  return dispatch('refreshJLoToken')
}

async function ensureJLoTokenAvailable({ state, dispatch }) {
  if (!state.JLoToken) {
    await dispatch('restoreJLoToken')
  }

  return dispatch('ensureValidJLoToken')
}

async function restoreStudioToken({ state, commit, dispatch }) {
  const tokenFromLocalStorage = getTokenFromLocalStorage('studio_token')
  const payload = getPayloadFromJwt(tokenFromLocalStorage?.access_token)

  if (
    payload?.organisation_id &&
    payload.organisation_id === state.selectedOrganisation?.id &&
    isTokenValid(tokenFromLocalStorage)
  ) {
    commit('SET_STUDIO_TOKEN', { token: tokenFromLocalStorage })

    setStudioTokenInServices(tokenFromLocalStorage)
    return
  }

  await dispatch('getStudioToken')
}

async function getStudioToken({ state, commit }) {
  const selectedOrganisationId = state.selectedOrganisation?.id

  if (!selectedOrganisationId) {
    throw new Error('Cannot generate studio token: no organisation selected')
  }

  const response = await OrganisationManagementService.postGenerateToken({ organisationId: selectedOrganisationId })

  const token = response?.data
  commit('SET_STUDIO_TOKEN', { token })
  setStudioTokenInServices(token)
}

async function authorize(_, { locale = 'en-US', stateParams = {} } = {}) {
  try {
    const authUri = await apis.oauth2.generateAuthUri(locale, stateParams)
    if (authUri) window.location.href = authUri
  } catch {
    apis.oauth2.reset()
  }
}

async function authenticate({ commit, dispatch }) {
  let state, tokenResponse
  try {
    state = apis.oauth2.getAuthorizeResponse(window.location.href)
    tokenResponse = await apis.oauth2.requestToken()
  } catch (error) {
    if (!(error instanceof AuthStateValidationError)) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'authenticate')
        Sentry.captureException(error)
      })
    }
    dispatch('logout')
    return
  }

  if (!state || !tokenResponse) {
    dispatch('logout')
    return
  }

  commit('SET_JLO_TOKEN', { token: tokenResponse })
  if (state.redirectFrom) {
    if (state.redirectFrom.includes('loginAsAdmin')) {
      appAnalytics.track(appAnalytics.events.USER_LOGGED_IN_FROM_LOGIN_AS_ADMIN_URL)
    } else {
      window.localStorage.setItem('fromDeepLink', 'true')
    }
  }

  router.replace({
    path: state.redirectFrom ? state.redirectFrom : '/',
    query: state.isReactivationOpen ? { isReactivationOpen: 'true' } : {},
  })
}

async function getOrganisationsList({ commit }) {
  try {
    const response = await OrganisationManagementService.getOrganisationsList()
    commit('SET_ORGANISATIONS', { organisations: response?.data?.organisations ?? [] })
  } catch (error) {
    if (error.response?.status === 404) {
      throw new UserNotFoundError()
    } else {
      throw new GetOrganisationListApiError(error)
    }
  }
}

function selectInitialSelectedOrganisation({ state, commit }) {
  const selectedOrgInLocalStorage = window.localStorage.getItem('selected_organisation_id')
  const organisationInState = state.organisations.find(org => org.id === selectedOrgInLocalStorage)
  const selectedOrganisation = organisationInState ?? state.organisations?.[0]
  commit('SET_SELECTED_ORGANISATION', { organisation: selectedOrganisation })
}

async function selectInitialSelectedAccommodationId({ dispatch }) {
  const selectedAccommodationIdInLocalStorage = parseInt(window.localStorage.getItem('selected_accommodation_id'))
  let accommodationIdInAssignments = null
  const assignmentsList = await dispatch('getAssignmentsList')

  if (!isNaN(selectedAccommodationIdInLocalStorage)) {
    accommodationIdInAssignments = assignmentsList?.find(
      assignment => assignment === selectedAccommodationIdInLocalStorage
    )
  }

  if (accommodationIdInAssignments) {
    setSelectedAccommodationId(accommodationIdInAssignments)
  } else {
    setSelectedAccommodationId(assignmentsList?.[0])
  }
}

async function switchOrganisation({ commit, dispatch }, { newOrganisation, targetAccommodationId = null }) {
  commit('SET_LOADING')
  commit('SET_SELECTED_ORGANISATION', { organisation: newOrganisation })

  const hasJLoToken = await dispatch('ensureJLoTokenAvailable')
  if (!hasJLoToken) {
    dispatch('logout')
    return
  }
  await dispatch('restoreStudioToken')

  if (!targetAccommodationId) {
    await dispatch('selectInitialSelectedAccommodationId')
  } else {
    setSelectedAccommodationId(targetAccommodationId)
  }

  await dispatch('restoreStudioSession')
}

async function getAssignmentsList({ state }) {
  const orgId = state.selectedOrganisation.id
  if (!orgId) return

  const orgAssignmentsQueryData = await queryClient.ensureQueryData({
    queryKey: ['organisationAssignments', orgId],
    queryFn: () => fetchOrganisationAssignments(orgId),
  })

  return orgAssignmentsQueryData
}

function logout(_, previousPath) {
  apis.oauth2.reset()
  const logoutUri = apis.oauth2.generateLogoutUri(previousPath)
  if (logoutUri) window.location.href = logoutUri
}

export default {
  authorize,
  restoreStudioSession,
  restoreJLoToken,
  refreshJLoToken,
  ensureValidJLoToken,
  ensureJLoTokenAvailable,
  logout,
  getOrganisationsList,
  selectInitialSelectedOrganisation,
  getStudioToken,
  restoreStudioToken,
  authenticate,
  switchOrganisation,
  getAssignmentsList,
  selectInitialSelectedAccommodationId,
}
